import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";

const Pill = ({ tone, children }) => {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900/50",
    amber: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50",
    red: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50",
    gray: "bg-zinc-50 text-zinc-600 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-800",
  };
  return (
    <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] rounded-sm ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function RegisteredUsers() {
  const [data, setData] = useState({ users: [], total: 0, page: 1, limit: 25, totalPages: 1 });
  const [metrics, setMetrics] = useState(null);
  const [search, setSearch] = useState("");
  const [verification, setVerification] = useState("all");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/users", {
        params: { page, limit: 25, search, verification, status, role },
      });
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, search, verification, status, role]);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/admin/users/metrics").then(({ data: m }) => setMetrics(m)).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [search, verification, status, role]);

  const openDetail = async (uid) => {
    try {
      const { data: u } = await api.get(`/admin/users/${uid}`);
      setDetail(u);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const selectCls = "h-9 border border-border rounded-sm px-3 text-sm bg-background";

  return (
    <div className="space-y-4" data-testid="registered-users-page">
      <div>
        <h2 className="text-xl font-bold tracking-tight">Registered Users</h2>
        <p className="text-xs text-zinc-500">Everyone who has registered on WorkMesh — admin view only.</p>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="registered-users-metrics">
          {[
            ["total", "Total Registered"],
            ["verified", "Verified"],
            ["pending_verification", "Pending Verification"],
            ["active", "Active"],
          ].map(([k, l]) => (
            <div key={k} className="border border-border p-3 bg-background" data-testid={`metric-${k}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l}</div>
              <div className="text-xl font-bold font-mono">{metrics[k]?.toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, company…"
            className="rounded-sm pl-8"
            data-testid="registered-users-search"
          />
        </div>
        <select value={verification} onChange={(e) => setVerification(e.target.value)} className={selectCls} data-testid="filter-verification">
          <option value="all">Verification: All</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending Verification</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="filter-status">
          <option value="all">Status: All</option>
          <option value="active">Active</option>
          <option value="pending_verification">Pending Verification</option>
          <option value="disabled">Disabled</option>
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value)} className={selectCls} data-testid="filter-role">
          <option value="all">Role: All</option>
          <option value="EMPLOYEE">Employee</option>
          <option value="TASK_OWNER">Task Owner</option>
          <option value="REVIEWER">Reviewer</option>
          <option value="MANAGER">Manager</option>
          <option value="ADMIN">Admin</option>
        </select>
      </div>

      <div className="border border-border bg-background overflow-x-auto">
        <table className="w-full text-sm" data-testid="registered-users-table">
          <thead className="border-b border-border">
            <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3 hidden md:table-cell">Email</th>
              <th className="text-left py-2 px-3 hidden lg:table-cell">Company</th>
              <th className="text-left py-2 px-3">Verified</th>
              <th className="text-left py-2 px-3">Status</th>
              <th className="text-left py-2 px-3 hidden md:table-cell">Role</th>
              <th className="text-left py-2 px-3 hidden lg:table-cell">Registered</th>
              <th className="text-left py-2 px-3 hidden lg:table-cell">Last Login</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr
                key={u.user_id}
                className="border-b border-border hover:bg-zinc-50 dark:hover:bg-zinc-900/50 cursor-pointer"
                onClick={() => openDetail(u.user_id)}
                data-testid={`registered-user-row-${u.user_id}`}
              >
                <td className="py-2 px-3 font-medium">{u.name}</td>
                <td className="py-2 px-3 text-zinc-500 hidden md:table-cell">{u.email}</td>
                <td className="py-2 px-3 text-zinc-500 hidden lg:table-cell">{u.company_name || "—"}</td>
                <td className="py-2 px-3">
                  {u.email_verified === false ? <Pill tone="amber">Pending</Pill> : <Pill tone="green">Verified</Pill>}
                </td>
                <td className="py-2 px-3">
                  {u.status === "disabled" ? <Pill tone="red">Disabled</Pill>
                    : u.status === "pending_verification" ? <Pill tone="amber">Pending</Pill>
                    : <Pill tone="green">Active</Pill>}
                </td>
                <td className="py-2 px-3 hidden md:table-cell"><Pill tone="gray">{u.role}</Pill></td>
                <td className="py-2 px-3 text-zinc-500 text-xs hidden lg:table-cell">{fmtDate(u.created_at)}</td>
                <td className="py-2 px-3 text-zinc-500 text-xs hidden lg:table-cell">{fmtDate(u.last_login_at)}</td>
              </tr>
            ))}
            {!loading && data.users.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-sm text-zinc-500" data-testid="registered-users-empty">No users match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span data-testid="registered-users-count">
          {data.total.toLocaleString()} users · page {data.page} of {data.totalPages}
        </span>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="registered-users-prev">
            <CaretLeft size={14} /> Prev
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)} data-testid="registered-users-next">
            Next <CaretRight size={14} />
          </Button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="registered-user-detail">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {[
                ["Email", detail.email],
                ["Company", detail.company_name || "—"],
                ["Role", detail.role],
                ["Verification", detail.email_verified === false ? "Pending Verification" : "Verified"],
                ["Account Status", detail.status === "disabled" ? "Disabled" : detail.status === "pending_verification" ? "Pending Verification" : "Active"],
                ["Registered", fmtDateTime(detail.created_at)],
                ["Verified At", fmtDateTime(detail.email_verified_at)],
                ["Last Login", fmtDateTime(detail.last_login_at)],
                ["Auth Provider", detail.auth_provider || "local"],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between gap-4 border-b border-border pb-1.5">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 pt-0.5">{l}</span>
                  <span className="text-right break-all">{v}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
